import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Briefcase, Image, ArrowLeftRight, Gift, User, Loader2 } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_CONFIG = {
  portfolio: { icon: Briefcase, color: theme.colors.primary, label: 'Portfolio' },
  nft: { icon: Image, color: theme.colors.accent.teal, label: 'NFT' },
  transfer: { icon: ArrowLeftRight, color: theme.colors.status.warning, label: 'Transfer' },
  utility: { icon: Gift, color: theme.colors.accent.salmon, label: 'Utility' },
  user: { icon: User, color: theme.colors.text.secondary, label: 'User' },
};

const SearchResultsPage = ({ user }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (query.length >= 2) {
      doSearch(query);
    } else {
      setResults([]);
      setSearched(false);
    }
  }, [query]);

  const doSearch = async (q) => {
    setLoading(true);
    setSearched(false);
    try {
      const res = await axios.get(`${API}/search`, { params: { q } });
      setResults(res.data.results || []);
    } catch (err) {
      console.error('Search failed:', err);
      setResults([]);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  // Group results by type
  const grouped = results.reduce((acc, r) => {
    acc[r.type] = acc[r.type] || [];
    acc[r.type].push(r);
    return acc;
  }, {});

  return (
    <div data-testid="search-results-page" style={{ padding: '40px', maxWidth: '900px' }}>
      <h1 style={{
        fontSize: '28px', fontWeight: 700,
        color: theme.colors.text.primary,
        fontFamily: theme.fonts.heading,
        marginBottom: '8px',
      }}>
        Search Results
      </h1>
      <p style={{ fontSize: '15px', color: theme.colors.text.secondary, marginBottom: '32px' }}>
        {query ? `Showing results for "${query}"` : 'Enter a search term in the header to begin.'}
      </p>

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '40px 0', justifyContent: 'center' }}>
          <Loader2 size={24} style={{ color: theme.colors.primary, animation: 'spin 1s linear infinite' }} />
          <span style={{ color: theme.colors.text.muted }}>Searching...</span>
        </div>
      )}

      {!loading && searched && results.length === 0 && (
        <div data-testid="search-no-results" style={{
          padding: '48px', textAlign: 'center',
          background: theme.colors.bg.secondary,
          borderRadius: theme.radius.card,
          border: `1px solid ${theme.colors.borders}`,
        }}>
          <Search size={40} style={{ color: theme.colors.text.muted, margin: '0 auto 12px' }} />
          <p style={{ color: theme.colors.text.muted, fontSize: '15px' }}>No results found for "{query}"</p>
        </div>
      )}

      {!loading && Object.keys(grouped).length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {Object.entries(grouped).map(([type, items]) => {
            const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.portfolio;
            const Icon = cfg.icon;
            return (
              <div key={type}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  marginBottom: '12px', paddingBottom: '8px',
                  borderBottom: `1px solid ${theme.colors.borders}`,
                }}>
                  <Icon size={18} style={{ color: cfg.color }} />
                  <span style={{
                    fontSize: '14px', fontWeight: 600,
                    color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.5px',
                  }}>
                    {cfg.label}s ({items.length})
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {items.map((item) => (
                    <div
                      key={item.id}
                      data-testid={`search-result-${item.type}-${item.id}`}
                      onClick={() => navigate(item.route)}
                      style={{
                        padding: '14px 18px',
                        background: theme.colors.bg.card,
                        border: `1px solid ${theme.colors.borders}`,
                        borderRadius: theme.radius.md,
                        cursor: 'pointer',
                        transition: 'box-shadow 0.2s, transform 0.15s',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = theme.colors.shadows.sm;
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}
                    >
                      <div>
                        <p style={{ fontSize: '15px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '2px' }}>
                          {item.title}
                        </p>
                        <p style={{ fontSize: '13px', color: theme.colors.text.muted }}>{item.subtitle}</p>
                      </div>
                      <span style={{
                        fontSize: '11px', fontWeight: 600, padding: '3px 10px',
                        borderRadius: '12px', textTransform: 'uppercase',
                        background: `${cfg.color}15`, color: cfg.color,
                      }}>
                        {cfg.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default SearchResultsPage;
